import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ManageSubscriptionRow from './ManageSubscriptionRow';
import { Modal } from '../../elements/Modal';

function getStatusColor(status) {
  const s = typeof status === 'string' ? status.toLowerCase() : '';
  if (s === 'active') return 't-bg-green-200 t-border-green-300 t-text-green-700';
  return 't-bg-gray-200 t-border-gray-300 t-text-gray-600';
}

function getStatusLabel(status, t) {
  const s = (status ?? '').toLowerCase();
  if (s === 'active') return t('CustomerSubs_Status_Active');
  if (s === 'trialing' || s === 'paused') return t('CustomerSubs_PausedBadge');
  if (s === 'canceled' || s === 'cancelled') return t('CustomerSubs_Status_Canceled');
  return status;
}

export default function ManageSubscriptionPanel({
  subscription,
  onPause,
  onChangeDeliveryDate,
  onResume,
  onCancel,
  onEditFormula,
  isFormulaLoading = false,
  onChangePlan,
  onRemoveItem,
  onClose,
  isPausing,
  isChangingDeliveryDate,
  isResuming,
  isCancelling,
  isRemoving,
}) {
  const { t } = useTranslation();
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  if (!subscription) return null;

  const effectiveStatus = (subscription.stripeData?.status ?? subscription.status ?? '').toLowerCase();
  const localStatus = String(subscription.status || '').toLowerCase();
  const isCancelled = ['cancelled', 'canceled'].includes(effectiveStatus) || ['cancelled', 'canceled'].includes(localStatus);
  const hasPauseCollection = Boolean(subscription.stripeData?.pause_collection);
  const isPaused =
    effectiveStatus === 'paused' ||
    effectiveStatus === 'trialing' ||
    localStatus === 'paused' ||
    hasPauseCollection;
  const productLabel = subscription.stripeData?.product ?? subscription.title;
  const items = subscription.items ?? [];
  const activeItems = items.filter((item) => (item.status ?? '').toLowerCase() === 'active');
  const hasItems = activeItems.length > 1;

  // Plan-change: Riise formula (see ALLOWED_PLAN_TRANSITIONS) or standalone Omega-3 (see OMEGA3_PLAN_TRANSITIONS on server).
  // We additionally require the row to not be in a paused/trialing state so the customer doesn't
  // accidentally combine pause + change.
  const productType = String(subscription.product_type || '').toLowerCase();
  const durationNum = parseInt(String(subscription.duration ?? ''), 10);
  const isOneOff = [30, 90].includes(durationNum);
  const canChangeRiise = productType === 'riise_formula' && [3, 6].includes(durationNum);
  const canChangeOmega3 = productType === 'omega-3' && [1, 3].includes(durationNum);
  const canChangePlan =
    typeof onChangePlan === 'function' &&
    (canChangeRiise || canChangeOmega3) &&
    !isPaused &&
    !isCancelled;
  // Pause / cancel only make sense on recurring subs with a live Stripe sub
  // (one-off 30/90 rows have no Stripe subscription to pause or cancel).
  const hasPendingFormulaUpdate = subscription.pendingFormulaUpdate?.type === 'formula';
  const hasStripeSubscription = Boolean(subscription.stripeData?.id || subscription.payment_subId);
  const canPauseOrCancel = !isOneOff && !isCancelled && hasStripeSubscription;
  const canChangeDeliveryDate =
    canPauseOrCancel && typeof onChangeDeliveryDate === 'function';

  const handleRemoveConfirm = async (itemId) => {
    const didRemove = await onRemoveItem?.(itemId);
    if (didRemove) {
      setConfirmRemoveId(null);
    }
  };

  return (
    <div className="t-mt-4 t-rounded-xl t-border t-border-gray-200 t-bg-white t-overflow-hidden t-shadow-sm t-w-full t-max-w-full">
      {/* Header */}
      <div className="t-relative t-flex t-flex-col sm:t-flex-row sm:t-items-center sm:t-justify-between t-gap-2 t-border-b t-border-gray-100 t-px-4 t-py-3 sm:t-px-5">
        <div className="t-flex t-items-center t-gap-2 t-flex-wrap t-min-w-0">
          <h4 className="t-text-base t-font-semibold t-m-0 t-break-words">
            {t('CustomerSubs_ManagePanel_Title', { product: productLabel })}
          </h4>
          <span
            className={`t-px-2 t-py-0.5 t-rounded-md t-border-2 t-capitalize t-text-xs t-font-medium t-flex-shrink-0 t-whitespace-nowrap ${getStatusColor(
              effectiveStatus,
            )}`}
          >
            {getStatusLabel(effectiveStatus, t)}
          </span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="t-absolute t-top-2.5 t-right-3 sm:t-static t-text-gray-400 hover:t-text-gray-600 t-transition-colors t-flex-shrink-0 t-text-xl t-leading-none"
            aria-label={t('CustomerSubs_ManagePanel_Close')}
          >
            ×
          </button>
        )}
      </div>

      <div className="t-px-4 t-py-4 sm:t-px-5 t-space-y-5">
        {hasPendingFormulaUpdate && <p role="status" className="t-text-sm t-text-gray-600">
          {t('DashboardFormula_Pending', { date: new Date(subscription.pendingFormulaUpdate.effectiveAt).toLocaleDateString() })}
        </p>}
        {/* Items section */}
        {hasItems && (
          <div>
            <h5 className="t-text-sm t-font-semibold t-text-gray-900 t-m-0 t-mb-2">
              {t('CustomerSubs_ManagePanel_Items_Title')}
            </h5>
            <div className="t-flex t-flex-col t-gap-2">
              {activeItems.map((item) => {
                const isItemActive = (item.status ?? '').toLowerCase() === 'active';
                const isConfirming = confirmRemoveId === item.id;

                return (
                  <div
                    key={item.id}
                    className="t-flex t-flex-col sm:t-flex-row sm:t-items-center sm:t-justify-between t-gap-2 t-rounded-lg t-border t-border-gray-100 t-bg-gray-50 t-px-3 t-py-2.5"
                  >
                    <div className="t-flex t-flex-col t-min-w-0">
                      <span className="t-text-sm t-font-medium t-text-gray-900 t-break-words t-capitalize">
                        {item.product_name}
                      </span>
                      <span className="t-text-xs t-text-gray-500 t-capitalize">
                        {item.product_type}
                      </span>
                    </div>
                    <div className="t-flex-shrink-0 t-w-full sm:t-w-auto">
                      {!isItemActive ? (
                        <span className="t-text-xs t-text-gray-400 t-capitalize">{item.status}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveId(item.id)}
                          disabled={isRemoving || isConfirming || hasPendingFormulaUpdate}
                          className="t-inline-flex t-items-center t-justify-center t-rounded-full t-px-2.5 t-py-1 t-text-xs t-font-semibold t-bg-white t-text-red-500 t-border t-border-red-300 hover:t-bg-red-50 t-transition-colors t-w-full sm:t-w-auto"
                        >
                          {t('CustomerSubs_ManagePanel_RemoveItem')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sub-level actions */}
        <div>
          <h5 className="t-text-sm t-font-semibold t-text-gray-900 t-m-0 t-mb-2">
            {t('CustomerSubs_Manage_Title')}
          </h5>
          <p className="t-text-xs t-text-gray-600 t-m-0 t-mb-3">
            {t('CustomerSubs_Manage_Subtitle')}
          </p>
          <div className="t-flex t-flex-col t-gap-2">
            {canChangePlan && (
              <ManageSubscriptionRow
                title={t('CustomerSubs_ChangePlan_Title')}
                description={t('CustomerSubs_ChangePlan_Desc')}
                buttonLabel={t('CustomerSubs_ChangePlan_Button')}
                variant="secondary"
                onClick={onChangePlan}
                disabled={hasPendingFormulaUpdate}
              />
            )}
            {canPauseOrCancel && (
              <>
                {isPaused ? (
                  <>
                    {canChangeDeliveryDate && (
                      <ManageSubscriptionRow
                        title={t('CustomerSubs_ChangeDeliveryDate_Title')}
                        description={t('CustomerSubs_ChangeDeliveryDate_Desc')}
                        buttonLabel={
                          isChangingDeliveryDate
                            ? t('CustomerSubs_ChangeDeliveryDate_Button_Loading')
                            : t('CustomerSubs_ChangeDeliveryDate_Button')
                        }
                        variant="secondary"
                        onClick={onChangeDeliveryDate}
                        disabled={isChangingDeliveryDate || hasPendingFormulaUpdate}
                      />
                    )}
                    <ManageSubscriptionRow
                      title={t('CustomerSubs_Resume_Title')}
                      description={t('CustomerSubs_Resume_Desc')}
                      buttonLabel={
                        isResuming ? t('CustomerSubs_Resume_Button_Loading') : t('CustomerSubs_Resume_Button')
                      }
                      variant="primary"
                      onClick={onResume}
                      disabled={isResuming || hasPendingFormulaUpdate}
                    />
                  </>
                ) : (
                  <>
                    {canChangeDeliveryDate && (
                      <ManageSubscriptionRow
                        title={t('CustomerSubs_ChangeDeliveryDate_Title')}
                        description={t('CustomerSubs_ChangeDeliveryDate_Desc')}
                        buttonLabel={
                          isChangingDeliveryDate
                            ? t('CustomerSubs_ChangeDeliveryDate_Button_Loading')
                            : t('CustomerSubs_ChangeDeliveryDate_Button')
                        }
                        variant="secondary"
                        onClick={onChangeDeliveryDate}
                        disabled={isChangingDeliveryDate || hasPendingFormulaUpdate}
                      />
                    )}
                    <ManageSubscriptionRow
                      title={t('CustomerSubs_Pause_Title')}
                      description={t('CustomerSubs_Pause_Desc')}
                      buttonLabel={isPausing ? t('CustomerSubs_Pause_Button') : t('CustomerSubs_Pause_Button')}
                      variant="primary"
                      onClick={onPause}
                      disabled={isPausing || hasPendingFormulaUpdate}
                    />
                  </>
                )}
                <ManageSubscriptionRow
                  title={t('CustomerSubs_Cancel_Title')}
                  description={t('CustomerSubs_Cancel_Desc')}
                  buttonLabel={
                    isCancelling ? t('CustomerSubs_Cancel_Button_Loading') : t('CustomerSubs_Cancel_Button')
                  }
                  variant="danger"
                  onClick={onCancel}
                  disabled={isCancelling}
                />
                {isFormulaLoading && (
                  <p role="status" className="t-m-0 t-text-sm t-text-gray-600">
                    {t('CustomerSubs_FormulaLoading')}
                  </p>
                )}
                {!isFormulaLoading && typeof onEditFormula === 'function' && (
                  <ManageSubscriptionRow
                    title={t('DashboardFormula_Edit')}
                    description={t('CustomerSubs_EditFormula_Desc')}
                    buttonLabel={t('DashboardFormula_Edit')}
                    variant="secondary"
                    onClick={onEditFormula}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={Boolean(confirmRemoveId)}
        handleClose={() => setConfirmRemoveId(null)}
        className="!t-max-w-[95vw] lg:t-max-w-[420px]"
      >
        <div className="t-p-6 t-flex t-flex-col t-gap-4 t-max-w-lg">
          <h2 className="t-text-xl t-font-bold t-text-center t-m-0">
            {t('CustomerSubs_ManagePanel_RemoveItem')}
          </h2>
          <p className="t-text-sm t-text-gray-700 t-text-center t-m-0">
            {t('CustomerSubs_ManagePanel_RemoveItem_Confirm')}
          </p>
          <div className="t-flex t-flex-col sm:t-flex-row t-items-stretch sm:t-items-center t-gap-3 t-mt-2">
            <button
              type="button"
              className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60"
              onClick={() => handleRemoveConfirm(confirmRemoveId)}
              disabled={isRemoving}
            >
              {t('CustomerSubs_ManagePanel_RemoveItem_Yes')}
            </button>
            <button
              type="button"
              className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-red-400 t-text-white hover:t-bg-red-500 disabled:t-opacity-60"
              onClick={() => setConfirmRemoveId(null)}
              disabled={isRemoving}
            >
              {t('CustomerSubs_ManagePanel_RemoveItem_No')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
